<script>
import {useThemeStore} from "@js/store/ThemeStore.js";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import CatalogFilter from "@js/components/Sections/Catalog/CatalogFilter.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import CatalogFilterTopBar from "@js/components/Sections/Catalog/Components/CatalogFilterTopBar.vue";
import CatalogProductItem from "@js/components/Sections/Catalog/Components/CatalogProductItem.vue";
import CatalogProductSkeleton from "@js/components/Sections/Catalog/Components/CatalogProductSkeleton.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import CatalogMobileFixedTopBar from "@js/components/Sections/Catalog/Components/CatalogMobileFixedTopBar.vue";
import CatalogMobileFilter from "@js/components/Sections/Catalog/CatalogMobileFilter.vue";
import Pagination from "@js/components/Pagination/Pagination.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import CatalogSearchNotFound from "@js/components/Sections/Catalog/Components/CatalogSearchNotFound.vue";
import CallbackButton from "@js/components/Buttons/CallbackButton.vue";
import CatalogFixedBlock from "@js/components/Sections/Catalog/Components/CatalogFixedBlock.vue";
import IconX from "@js/components/Icons/IconX.vue";
import CatalogSelectedFilterTags from "@js/components/Sections/Catalog/CatalogSelectedFilterTags.vue";
import IconTrash from "@js/components/Icons/IconTrash.vue";

export default {
    name: "CatalogPage",
    components: {
        IconTrash,
        CatalogSelectedFilterTags,
        IconX,
        CatalogFixedBlock,
        CallbackButton,
        CatalogSearchNotFound,
        Breadcrumbs,
        Pagination,
        CatalogMobileFilter,
        CatalogMobileFixedTopBar,
        OnlyMobile,
        CatalogProductSkeleton,
        CatalogProductItem,
        CatalogFilterTopBar,
        OnlyDesktop,
        CatalogFilter
    },
    watch: {
        'catalogStore.filter': {
            handler: function () {
                clearTimeout(this.getCatalogTimeout);
                this.getCatalogTimeout = setTimeout(() => {
                    this.catalogStore.pagination.current_page = this.catalogStore.getParamFromUrl('page', 1);
                    this.catalogStore.getCatalog();
                }, 300);
            },
            deep: true,
        },
        'catalogStore.pagination.current_page': function () {
            clearTimeout(this.getCatalogTimeout);
            this.getCatalogTimeout = setTimeout(() => {
                this.catalogStore.getCatalog();
            }, 300);
        },
        'catalogStore.products': {
            handler: function () {
                this.insertSchemaMarkup();
            },
            deep: true,
        }
    },
    beforeMount() {
        this.themeStore.activateLightTheme();
        this.catalogStore.syncUrlParamsWithState();
    },
    beforeUnmount() {
        this.removeSchemaMarkup()
    },
    methods: {
        insertSchemaMarkup() {
            if (typeof window !== "undefined") {
                const scriptId = "schema-catalog-jsonld"
                let script = document.getElementById(scriptId)

                if (script) {
                    script.remove()
                }

                script = document.createElement("script")
                script.id = scriptId
                script.type = "application/ld+json"
                script.textContent = JSON.stringify({
                    "@context": "https://schema.org/",
                    "@type": "OfferCatalog",
                    "name": "Каталог ЖБИ",
                    "url": "https://dorozhnyeplity.ru/catalog",
                    "itemListElement": this.catalogStore.products.map((product, index) => ({
                        "@type": "Offer",
                        "name": product.name ?? "Продукт",
                        "description": product.description ?? "Описание отсутствует",
                        "sku": product.sku ?? "Не указан",
                        "image": product.image !== null
                            ? `/storage/products/${product.image.path}/cover/${product.image.order[0]}`
                            : "/assets/images/defaults/product-not-found.webp",
                        "priceCurrency": "RUB",
                        "price": product.price ?? "0",
                        "url": route('product.show', {path: product.full_path}),
                        "availability": product.availability
                            ? "https://schema.org/InStock"
                            : "https://schema.org/OutOfStock"
                    }))
                })

                document.head.appendChild(script)
            }
        },
        removeSchemaMarkup() {
            const script = document.getElementById("schema-catalog-jsonld")
            if (script) {
                script.remove()
            }
        }
    },
    setup() {
        const themeStore = useThemeStore()
        const catalogStore = useCatalogStore()

        return {themeStore, catalogStore}
    }
}
</script>

<template>
    <OnlyMobile>
        <CatalogMobileFixedTopBar/>
        <CatalogMobileFilter/>
    </OnlyMobile>
    <div class="pt-20 md:pt-10">
        <div class="container mx-auto">
            <div class="mb-2 md:mb-5">
                <div class="flex flex-col">
                    <h1 class="text-2xl md:text-2xl font-bold text-black sm:mb-2 order-2 sm:order-1">
                        Каталог ЖБИ
                    </h1>
                    <Breadcrumbs class="order-1 sm:order-2 mb-2 sm:mb-0"/>
                </div>
                <hr class="mt-3 hidden md:block"/>
            </div>
            <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-black">
                <div class="hidden md:block col-span-12 md:col-span-4 lg:col-span-3">
                    <OnlyDesktop>
                        <CatalogFilter class="mb-10"/>
                        <div class="sticky top-5 bg-white shadow-md z-50">
                            <CatalogFixedBlock/>
                        </div>
                    </OnlyDesktop>
                </div>
                <div class="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col gap-3 md:gap-5">
                    <CatalogFilterTopBar/>
                    <OnlyDesktop v-if="catalogStore.selected_filters.length > 0">
                        <div
                            class="w-full p-0 md:px-5 md:py-2 md:bg-white md:border md:border-gray-200 md:shadow rounded-xl">
                           <div class="flex items-center flex-wrap">
                               <div @click.prevent="catalogStore.resetFilter"
                                    class="group flex items-center justify-center transition cursor-pointer w-[40px] h-[40px] p-0.5 border border-gray-800 hover:bg-indigo-600 hover:border-indigo-600 rounded-full">
                                   <IconTrash class="w-5 h-5 text-gray-700 group-hover:text-white transition"/>
                               </div>
                               <div class="ms-3">
                                   <div class="vertical-divider"></div>
                               </div>
                               <CatalogSelectedFilterTags class="ms-5"/>
                           </div>
                        </div>
                    </OnlyDesktop>
                    <CatalogSearchNotFound
                        v-if="catalogStore.loadings.catalog_loaded && !catalogStore.products.length"/>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                        <CatalogProductItem v-if="catalogStore.loadings.catalog_loaded"
                                            v-for="(data, key) in catalogStore.products"
                                            :data="data" :key="key"/>
                        <CatalogProductSkeleton v-else v-for="i in 9"/>
                    </div>
                    <div class="row d-flex align-items-center justify-content-center">
                        <Pagination :current-page="catalogStore.pagination.current_page"
                                    :total-pages="catalogStore.pagination.total_pages"
                                    @update:current-page="catalogStore.onPageChange"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
body {
    background: #f2f3f6;
}

.vertical-divider {
    width: 2px;
    border-radius: 100px;
    height: 2rem;
    background-color: #5850ec;
}
</style>
